/**
 * YD Video Scrub Section
 * 视频滚动控制动画区块
 *
 * 全程 scrub 模式: 滚动控制视频帧，标题按段切换
 * - segment1 (0~150vh): 帧 1→67，标题: intro
 * - segment2 (150~300vh): 帧 67→135，标题: inflow
 * - segment3 (300~450vh): 帧 135→202，标题: timescale
 * - segment4 (450~600vh): 帧 202→270，标题: countdown
 *
 * 移动端 (<768px): 使用序列帧 Canvas 渲染
 * 桌面端 (>=768px): 使用视频 Scrub
 */
(function() {
  'use strict';

  /**
   * 基础 Scrub 动画控制器
   * 共享移动端和桌面端的公共逻辑
   */
  class BaseScrubAnimator {
    constructor(config) {
      this.config = config;

      // DOM 元素
      this.section = null;
      this.wrapper = null;

      // 滚动参数
      this.totalScrollDistance = 0;
      this.scrollTrigger = null;

      // 标题元素缓存
      this.titleElements = {};
      this.splitTextInstances = {};
      this.revealedTitles = new Set();
      this.scrollTriggerReady = false;
      this.lastScrollVh = 0;
    }

    /**
     * 解析滚动高度配置
     */
    parseScrollHeight(value) {
      const viewportHeight = window.innerHeight;
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
        return (parseFloat(value) / 100) * viewportHeight;
      }
      return viewportHeight;
    }

    /**
     * 设置 section 高度
     * 注意：使用 pinSpacing: true 后，由 ScrollTrigger 自动管理间距，不再需要手动设置高度
     */
    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    /**
     * 缓存标题元素
     */
    cacheTitleElements() {
      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        if (titleConfig.id) {
          const el = document.querySelector(`#${titleConfig.id}`);
          if (el) {
            this.titleElements[titleConfig.id] = el;
            const hasNoFadeIn = !titleConfig.fadeIn;
            if (typeof gsap !== 'undefined') {
              gsap.set(el, { opacity: hasNoFadeIn ? 1 : 0 });
            }
          }
        }
      }
      this.initSplitText();
    }

    /**
     * 初始化 SplitText
     */
    initSplitText() {
      if (typeof SplitText === 'undefined') {
        return;
      }

      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        const container = this.titleElements[titleConfig.id];
        if (!container) continue;

        const textElements = container.querySelectorAll('h1, h2, p');
        const targets = textElements.length > 0 ? textElements : [container];

        const instances = [];
        targets.forEach(el => {
          const split = new SplitText(el, {
            type: 'lines',
            linesClass: 'yd-line'
          });

          split.lines.forEach(line => {
            const wrapper = document.createElement('div');
            wrapper.className = 'yd-line-inner';
            wrapper.innerHTML = line.innerHTML;
            line.innerHTML = '';
            line.appendChild(wrapper);
          });

          instances.push(split);
          gsap.set(el.querySelectorAll('.yd-line-inner'), { yPercent: 100 });
        });

        this.splitTextInstances[titleConfig.id] = instances;
      }
    }

    /**
     * 播放 Line Reveal 动画
     */
    playLineReveal(titleId) {
      if (this.revealedTitles.has(titleId)) return;

      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.add(titleId);

      gsap.fromTo(inners,
        { yPercent: 100 },
        { yPercent: 0, duration: 0.8, stagger: 0.12, ease: 'power3.out' }
      );
    }

    /**
     * 重置 Line Reveal 状态
     */
    resetLineReveal(titleId) {
      if (!this.revealedTitles.has(titleId)) return;

      const container = this.titleElements[titleId];
      if (!container) return;

      const inners = container.querySelectorAll('.yd-line-inner');
      if (inners.length === 0) return;

      this.revealedTitles.delete(titleId);
      gsap.set(inners, { yPercent: 100 });
    }

    /**
     * 为没有 fadeIn 配置的标题触发初始 Line Reveal
     */
    triggerInitialLineReveal() {
      const titles = this.config.titles || [];
      for (const titleConfig of titles) {
        if (!titleConfig.fadeIn && this.titleElements[titleConfig.id]) {
          this.playLineReveal(titleConfig.id);
        }
      }
    }

    /**
     * 基于滚动 vh 更新所有标题
     */
    updateTitles(scrollVh) {
      const titles = this.config.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        let opacity = 1;

        if (titleConfig.fadeIn) {
          const { startVh, endVh } = titleConfig.fadeIn;
          if (scrollVh < startVh) {
            opacity = 0;
          } else if (scrollVh < endVh) {
            opacity = (scrollVh - startVh) / (endVh - startVh);
          }
        }

        if (titleConfig.fadeOut && opacity > 0) {
          const { startVh, endVh } = titleConfig.fadeOut;
          if (scrollVh >= endVh) {
            opacity = 0;
          } else if (scrollVh > startVh) {
            opacity = 1 - (scrollVh - startVh) / (endVh - startVh);
          }
        }

        if (opacity === 0 && this.revealedTitles.has(titleConfig.id)) {
          this.resetLineReveal(titleConfig.id);
        }

        if (opacity > 0 && !this.revealedTitles.has(titleConfig.id)) {
          if (titleConfig.fadeIn) {
            this.playLineReveal(titleConfig.id);
          } else if (opacity === 1 && this.scrollTriggerReady) {
            this.playLineReveal(titleConfig.id);
          }
        }

        if (typeof gsap !== 'undefined') {
          gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
        }
      }
    }

    /**
     * 等待布局稳定
     * 确保所有动态高度都已计算完成，上游 section 也已初始化
     * 注意：不再单独调用 ScrollTrigger.refresh()，依赖全局统一刷新
     * @returns {Promise<void>}
     */
    waitForLayoutStable() {
      return new Promise((resolve) => {
        // 双重 rAF 确保布局已完成
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // 不再单独调用 ScrollTrigger.refresh()
            // 依赖初始化顺序和 theme_yd.liquid 中的统一刷新
            resolve();
          });
        });
      });
    }

    /**
     * 销毁
     */
    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }

  /**
   * 移动端序列帧动画控制器
   */
  class FrameSequenceAnimator extends BaseScrubAnimator {
    constructor(config) {
      super(config);

      this.canvas = null;
      this.ctx = null;
      this.frames = [];
      this.totalFrames = config.totalFrames || 271;
      this.baseUrl = 'https://cf-files.dotnfilm.studio/gravity_web/frames/man_mobile';
      this.useAvif = false;
      this.isReady = false;
    }

    /**
     * 检测 AVIF 支持
     */
    async checkAvifSupport() {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABpAQ0AIyDkAgQAAAABgAAAD/';
      });
    }

    /**
     * 格式化帧号
     */
    formatFrameNumber(frame) {
      return `1_${String(frame).padStart(5, '0')}`;
    }

    /**
     * 加载单帧图片
     */
    loadFrame(frameIndex, folder, format) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.frames[frameIndex] = img;
          resolve(img);
        };
        img.onerror = () => {
                    resolve(null);
        };
        img.src = `${folder}/${this.formatFrameNumber(frameIndex)}.${format}`;
      });
    }

    /**
     * 预加载所有帧
     */
    async preloadFrames() {
      const format = this.useAvif ? 'avif' : 'webp';
      const folder = `${this.baseUrl}/${format}`;

      
      // 首批加载前 50 帧
      const firstBatch = [];
      for (let i = 0; i < Math.min(50, this.totalFrames); i++) {
        firstBatch.push(this.loadFrame(i, folder, format));
      }
      await Promise.all(firstBatch);

      const firstBatchLoaded = this.frames.filter(f => f !== null && f !== undefined).length;
      
      if (this.frames[0]) {
        this.renderFrame(0);
      }

      // 后台加载剩余帧
      const remaining = [];
      for (let i = 50; i < this.totalFrames; i++) {
        remaining.push(this.loadFrame(i, folder, format));
      }
      await Promise.all(remaining);

      const totalLoaded = this.frames.filter(f => f !== null && f !== undefined).length;
      
      this.isReady = true;
    }

    /**
     * Canvas 渲染帧 (cover 效果)
     */
    renderFrame(frameIndex) {
      const img = this.frames[frameIndex];
      if (!img || !this.ctx) return;

      const width = this.canvasWidth;
      const height = this.canvasHeight;

      this.ctx.clearRect(0, 0, width, height);

      const scale = Math.max(width / img.width, height / img.height);
      const x = (width - img.width * scale) / 2;
      const y = (height - img.height * scale) / 2;

      this.ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
    }

    /**
     * 设置 Canvas 尺寸
     */
    setupCanvas() {
      // 移动端强制 DPR = 1，桌面端最大 2（性能优化）
      const isMobile = window.innerWidth < 768;
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);
      const rect = this.wrapper.getBoundingClientRect();

      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;

      this.canvas.width = rect.width * dpr;
      this.canvas.height = rect.height * dpr;
      this.canvas.style.width = `${rect.width}px`;
      this.canvas.style.height = `${rect.height}px`;

      this.ctx = this.canvas.getContext('2d');
      this.ctx.scale(dpr, dpr);
    }

    /**
     * 创建 ScrollTrigger
     */
    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
                return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.wrapper,
        start: 'top top',
        end: `+=${this.totalScrollDistance}`,
        pin: this.wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 5,  // 中等优先级，在 product-sequence(10) 之后，product-sequence-2(0) 之前
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const scrollVh = self.progress * 600;
          this.lastScrollVh = scrollVh;

          const frame = Math.round(self.progress * (this.totalFrames - 1));
          this.renderFrame(frame);
          this.updateTitles(scrollVh);
        },
        onEnter: () => this.triggerInitialLineReveal(),
        onEnterBack: () => this.triggerInitialLineReveal()
      });

      this.scrollTriggerReady = true;
    }

    /**
     * 初始化 - 先创建 ScrollTrigger 确保滚动位置正确，再后台加载帧
     */
    async initialize() {

      this.canvas = document.querySelector('#video-scrub-canvas');
      this.section = document.querySelector('#video-scrub-section');
      this.wrapper = document.querySelector('#video-scrub-wrapper');

      if (!this.canvas || !this.section || !this.wrapper) {
                return;
      }

      // 1. 先设置基础参数和 UI
      this.setupCanvas();
      this.totalScrollDistance = this.parseScrollHeight(this.config.scrollHeight || '600svh');
      this.cacheTitleElements();
      this.setupScrollSpacer();
      this.updateTitles(0);

      // 2. 立即创建 ScrollTrigger（不等待帧加载）
      // 这样用户滚动时 pin 位置就是正确的
      await this.waitForLayoutStable();
      this.createScrollTrigger();

      // 3. 后台加载帧（不阻塞）
      this.useAvif = await this.checkAvifSupport();
      this.preloadFrames().then(() => {
        // 帧加载完成后，渲染当前 progress 对应的帧
        if (this.frames[0]) {
          const currentFrame = Math.round((this.lastScrollVh / 600) * (this.totalFrames - 1));
          this.renderFrame(Math.max(0, Math.min(this.totalFrames - 1, currentFrame)));
        }
      });

      window.addEventListener('resize', () => {
        this.setupCanvas();
        const currentFrame = Math.round((this.lastScrollVh / 600) * (this.totalFrames - 1));
        this.renderFrame(Math.max(0, Math.min(this.totalFrames - 1, currentFrame)));
      });

          }

    /**
     * 销毁
     */
    dispose() {
      super.dispose();
      this.frames = [];
    }
  }

  /**
   * 视频 Scrub 动画控制器
   */
  class VideoScrubAnimator extends BaseScrubAnimator {
    constructor(config) {
      super(config);

      this.video = null;
      this.fps = config.fps || 25;
      this.totalFrames = config.totalFrames || 270;
      this.duration = this.totalFrames / this.fps;
    }

    /**
     * 帧数转时间 (秒)
     */
    frameToTime(frame) {
      return (frame - 1) / this.fps;
    }

    /**
     * 等待视频加载就绪（等待 canplaythrough 而非仅 loadedmetadata）
     * @returns {Promise<boolean>} 视频是否加载成功
     */
    waitForVideoLoad() {
      return new Promise((resolve) => {
        // 优先检查 readyState >= 3 (HAVE_FUTURE_DATA) 或 4 (HAVE_ENOUGH_DATA)
        if (this.video.readyState >= 3) {
                    resolve(true);
          return;
        }

        let resolved = false;

        const onCanPlay = () => {
          if (resolved) return;
          resolved = true;
          cleanup();
                    resolve(true);
        };

        const onError = (e) => {
          if (resolved) return;
          resolved = true;
          cleanup();
                    resolve(false);
        };

        const cleanup = () => {
          this.video.removeEventListener('canplaythrough', onCanPlay);
          this.video.removeEventListener('canplay', onCanPlay);
          this.video.removeEventListener('error', onError);
        };

        this.video.addEventListener('canplaythrough', onCanPlay);
        this.video.addEventListener('canplay', onCanPlay);
        this.video.addEventListener('error', onError);

        // 超时处理 - 15 秒
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            cleanup();
            // 超时时检查当前状态
            if (this.video.readyState >= 2) {
                            resolve(true);
            } else {
                            resolve(false);
            }
          }
        }, 15000);
      });
    }

    /**
     * 验证视频 seek 能力
     * @returns {Promise<boolean>} 视频是否可以正常 seek
     */
    verifySeekCapability() {
      return new Promise((resolve) => {
        let resolved = false;

        const onSeeked = () => {
          if (resolved) return;
          resolved = true;
          this.video.removeEventListener('seeked', onSeeked);
                    resolve(true);
        };

        this.video.addEventListener('seeked', onSeeked);

        // 尝试 seek 到第 0 帧
        try {
          this.video.currentTime = 0;
        } catch (e) {
          resolved = true;
          this.video.removeEventListener('seeked', onSeeked);
                    resolve(false);
          return;
        }

        // 500ms 超时
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            this.video.removeEventListener('seeked', onSeeked);
            // 如果超时但 currentTime 确实是 0，也算成功
            const seekSuccess = Math.abs(this.video.currentTime) < 0.1;
            if (seekSuccess) {
                          } else {
                          }
            resolve(seekSuccess);
          }
        }, 500);
      });
    }

    /**
     * 创建 ScrollTrigger
     */
    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
                return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      this.scrollTrigger = ScrollTrigger.create({
        trigger: this.wrapper,
        start: 'top top',
        end: `+=${this.totalScrollDistance}`,
        pin: this.wrapper,
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 5,  // 中等优先级，在 product-sequence(10) 之后，product-sequence-2(0) 之前
        invalidateOnRefresh: true,
        onUpdate: (self) => this.handleScrollUpdate(self.progress),
        onEnter: () => this.triggerInitialLineReveal(),
        onEnterBack: () => this.triggerInitialLineReveal()
      });

      this.scrollTriggerReady = true;
    }

    /**
     * 滚动更新处理 - 全程 scrub 模式
     */
    handleScrollUpdate(progress) {
      const scrollVh = progress * 600;
      this.lastScrollVh = scrollVh;

      // 只有视频就绪后才执行 seek
      if (this.videoReady && this.video) {
        const frame = 1 + (progress * (this.totalFrames - 1));
        this.video.currentTime = this.frameToTime(Math.min(this.totalFrames, Math.max(1, frame)));
      }

      this.updateTitles(scrollVh);
    }

    /**
     * 初始化 - 先创建 ScrollTrigger 确保滚动位置正确，视频加载后台进行
     */
    async initialize() {

      this.video = document.querySelector('#video-scrub-element');
      this.section = document.querySelector('#video-scrub-section');
      this.wrapper = document.querySelector('#video-scrub-wrapper');

      if (!this.video || !this.section || !this.wrapper) {
                return;
      }

      // 1. 先设置基础参数和 UI
      this.totalScrollDistance = this.parseScrollHeight(this.config.scrollHeight || '600svh');
      this.cacheTitleElements();
      this.setupScrollSpacer();
      this.updateTitles(0);

      // 2. 立即创建 ScrollTrigger（不等待视频加载）
      // 这样用户滚动时 pin 位置就是正确的
      await this.waitForLayoutStable();
      this.createScrollTrigger();

      // 3. 标记视频未就绪，等待加载
      this.videoReady = false;

      // 4. 后台等待视频加载（不阻塞）
      this.waitForVideoLoad().then(async (videoLoaded) => {
        if (!videoLoaded) {
                    return;
        }

        // 验证 seek 能力
        const canSeek = await this.verifySeekCapability();
        if (!canSeek) {
                    return;
        }

        this.video.currentTime = 0;
        this.video.pause();
        this.videoReady = true;

        // 视频就绪后，seek 到当前 progress 对应的位置
        if (this.lastScrollVh > 0) {
          const frame = 1 + ((this.lastScrollVh / 600) * (this.totalFrames - 1));
          this.video.currentTime = this.frameToTime(Math.min(this.totalFrames, Math.max(1, frame)));
        }
      });

          }

    /**
     * 销毁
     */
    dispose() {
      super.dispose();
    }
  }

  /**
   * 等待依赖加载
   */
  function waitForDependencies() {
    return new Promise((resolve) => {
      let resolved = false;

      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          if (!resolved) {
            resolved = true;
            gsap.registerPlugin(ScrollTrigger);
            resolve(true);
          }
        }
      }, 100);

      setTimeout(() => {
        clearInterval(check);
        if (!resolved) {
          resolved = true;
                    resolve(false);
        }
      }, 15000);
    });
  }

  /**
   * 等待 product-sequence 进入 INTRO_LOOP 状态
   * 确保上游 section 的 ScrollTrigger pin 已计算完成
   */
  function waitForProductSequenceReady() {
    return new Promise((resolve) => {
      // 检查是否已经 ready（状态机已进入 INTRO_LOOP 或更后的状态）
      const stateMachine = window.ydProductSequenceStateMachine;
      if (stateMachine && ['INTRO_LOOP', 'SCRUB', 'FINAL_LOOP'].includes(stateMachine.currentState)) {
                resolve();
        return;
      }

      // 监听 ready 事件
      const handler = (e) => {
        window.removeEventListener('yd-product-sequence-ready', handler);
                resolve();
      };
      window.addEventListener('yd-product-sequence-ready', handler);

      // 超时保护 - 8秒后强制初始化
      setTimeout(() => {
        window.removeEventListener('yd-product-sequence-ready', handler);
                resolve();
      }, 8000);
    });
  }

  /**
   * 初始化
   */
  async function init() {
    // 防止 HotReload 导致的重复初始化
    if (window.ydVideoScrubAnimator) {
            return;
    }

    const section = document.querySelector('#video-scrub-section');
    if (!section) {
      return;
    }

    const configData = section.dataset.config;
    if (!configData) {
            return;
    }

    const config = JSON.parse(configData);

    const depsLoaded = await waitForDependencies();
    if (!depsLoaded) {
            return;
    }

    // 等待 product-sequence 进入 INTRO_LOOP 状态
    // 确保上游 pin 位置已计算完成
    await waitForProductSequenceReady();

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
            const animator = new FrameSequenceAnimator(config);
      await animator.initialize();
      window.ydVideoScrubAnimator = animator;
    } else {
            const animator = new VideoScrubAnimator(config);
      await animator.initialize();
      window.ydVideoScrubAnimator = animator;
    }

    // 派发事件通知下游 section (product-sequence-2) 可以开始初始化
    window.dispatchEvent(new CustomEvent('yd-video-scrub-ready', {
      detail: { timestamp: Date.now(), isMobile }
    }));
      }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
